import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import { ProgressSpinner } from "primereact/progressspinner";
import { useJsApiLoader } from "@react-google-maps/api";
import apiFunctions from "../../services/api-service";
import useToast from "../../components/common/useToast";
import { ArrowLeftIcon, MegaphoneIcon } from "@heroicons/react/24/outline";

declare global {
  interface Window {
    Razorpay: any;
  }
}

const formatDate = (date: Date | null) => {
  if (!date) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${year}-${month}-${day}`;
};

const MyAdvertisements: React.FC = () => {
  const { userid } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyCEW1Wc11SUHoF3sgsxEq0M1XgOqSelP5M",
    libraries: ["places"]
  });

  const [ads, setAds] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"running" | "expired">("running");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [runningCount, setRunningCount] = useState(0);
  const [expiredCount, setExpiredCount] = useState(0);
  const itemsPerPage = 6;

  /* ---- Rerun modal state ---- */
  const [rerunAd, setRerunAd] = useState<any>(null);
  const [rerunCity, setRerunCity] = useState<any>(null);
  const [packageOptions, setPackageOptions] = useState<any[]>([]);
  const [fetchingPackages, setFetchingPackages] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<any>(null);
  const [paying, setPaying] = useState(false);
  const cityInputRef = useRef<HTMLInputElement>(null);

  const fetchAds = async () => {
    try {
      setLoading(true);
      const response = await apiFunctions.get(`/advertisement/mine/${userid}`, {
        params: { page: currentPage + 1, limit: itemsPerPage, tab: activeTab }
      });
      if (response?.data?.success) {
        setAds(response.data.data || []);
        setTotalPages(response.data.totalPages || 1);
        setRunningCount(response.data.runningCount || 0);
        setExpiredCount(response.data.expiredCount || 0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userid, activeTab, currentPage]);

  // Start fresh on page 1 whenever the tab changes
  const handleTabChange = (tab: "running" | "expired") => {
    setActiveTab(tab);
    setCurrentPage(0);
  };

  /* ---- Google Places autocomplete on the rerun modal's city field ---- */
  useEffect(() => {
    if (!isLoaded || !rerunAd || !cityInputRef.current) return;
    const timer = setTimeout(() => {
      if (!cityInputRef.current) return;
      const autocomplete = new (window as any).google.maps.places.Autocomplete(
        cityInputRef.current,
        { types: ["(cities)"] }
      );
      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (!place.place_id) return;
        const components = place.address_components || [];
        const getComp = (type: string) =>
          components.find((c: any) => c.types.includes(type))?.long_name || "";
        setRerunCity({
          name: place.name,
          formattedAddress: place.formatted_address,
          city: getComp("locality") || getComp("administrative_area_level_2"),
        });
      });
    }, 100);
    return () => clearTimeout(timer);
  }, [isLoaded, rerunAd]);

  const fetchPackagesForRerun = async (ad: any, city: any) => {
    try {
      setFetchingPackages(true);
      const response = await apiFunctions.post(
        `/advertisementpackage/dropdown?page=1&limit=50`,
        {
          section: ad.page_name || [],
          type: ad.ad_type || "",
          cityId: city?.city || ""
        }
      );
      setPackageOptions(response?.data?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setFetchingPackages(false);
    }
  };

  const openRerunModal = (ad: any) => {
    setSelectedPackage(null);
    setPackageOptions([]);
    if (cityInputRef.current) cityInputRef.current.value = "";
    setRerunCity(null);
    setRerunAd(ad);
  };

  const closeRerunModal = () => {
    setRerunAd(null);
    setRerunCity(null);
    setSelectedPackage(null);
    setPackageOptions([]);
  };

  // Fetch packages whenever the modal opens or the selected city changes (location-based pricing)
  useEffect(() => {
    if (!rerunAd) return;
    fetchPackagesForRerun(rerunAd, rerunCity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rerunAd, rerunCity]);

  const loadRazorpay = () => {
    return new Promise<boolean>((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayAndRerun = async () => {
    if (!rerunAd || !selectedPackage) {
      showToast({ severity: "warn", summary: "Package Required", detail: "Please select a package to continue", life: 3000 });
      return;
    }

    try {
      setPaying(true);

      const razorLoaded = await loadRazorpay();
      if (!razorLoaded) {
        showToast({ severity: "error", summary: "Error", detail: "Razorpay failed to load", life: 3000 });
        setPaying(false);
        return;
      }

      const start = new Date();
      const days = Number(selectedPackage.days);
      const end = new Date(start);
      end.setDate(start.getDate() + days);

      const orderRes = await axios.post(
        `${process.env.REACT_APP_API_URL}/advertisement/rerun-order`,
        { userid, amount: selectedPackage.price, adId: rerunAd._id }
      );

      const order = orderRes.data.order;

      const options = {
        key: "rzp_live_SxPm6WoawVwEun",
        amount: order.amount,
        currency: order.currency,
        order_id: order.id,
        name: "Sagar Samaj",
        description: "Rerun your advertisement",
        image: "https://sagarsamaj.in/static/media/logosmall.155d8b602123092d7a45.png",
        handler: async (response: any) => {
          try {
            setPaying(true);
            await axios.post(
              `${process.env.REACT_APP_API_URL}/advertisement/verify-rerun`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                adId: rerunAd._id,
                start_date: formatDate(start),
                end_date: formatDate(end),
                city: rerunCity,
              }
            );
            showToast({ severity: "success", summary: "Success", detail: "Advertisement rerun successfully!", life: 3000 });
            closeRerunModal();
            fetchAds();
          } catch (err) {
            console.error(err);
            showToast({ severity: "error", summary: "Error", detail: "Payment verification failed", life: 4000 });
          } finally {
            setPaying(false);
          }
        },
        prefill: { name: rerunAd.company_name },
        theme: { color: "#2563eb" },
        // User closed/exited the Razorpay checkout without paying — without
        // this, `paying` stays true forever and the popup is stuck on
        // "Processing..." with no way to close it.
        modal: {
          ondismiss: () => setPaying(false),
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => {
        showToast({ severity: "error", summary: "Payment Failed", detail: "Payment could not be completed. Please try again.", life: 4000 });
        setPaying(false);
      });
      rzp.open();

    } catch (err) {
      console.error(err);
      showToast({ severity: "error", summary: "Error", detail: "Error starting payment", life: 4000 });
      setPaying(false);
    }
  };

  const renderAdCard = (ad: any) => (
    <div key={ad._id} className="bg-white rounded-xl shadow p-4 flex flex-col gap-2">
      <div className="flex items-center gap-3">
        {ad.thumbnail_url || ad.media?.[0]?.media_url ? (
          <img
            src={ad.thumbnail_url || ad.media[0].media_url}
            alt={ad.company_name}
            className="w-16 h-16 rounded-lg object-cover border"
          />
        ) : (
          <div className="w-16 h-16 rounded-lg border flex items-center justify-center bg-gray-50">
            <MegaphoneIcon className="w-8 h-8 text-gray-300" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-gray-800 truncate">{ad.company_name}</div>
          <Tag
            value={ad.is_active ? "Running" : "Expired"}
            severity={ad.is_active ? "success" : "danger"}
            className="mt-1"
          />
        </div>
      </div>

      <div className="text-sm text-gray-500 mt-1">
        {ad.start_date && (
          <div>Start: {new Date(ad.start_date).toLocaleDateString("en-GB")}</div>
        )}
        {ad.end_date && (
          <div>End: {new Date(ad.end_date).toLocaleDateString("en-GB")}</div>
        )}
      </div>

      {!ad.is_active && (
        <Button
          label="Rerun Advertisement"
          className="mt-2 w-full"
          onClick={() => openRerunModal(ad)}
        />
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-blue-600 text-white py-3 px-12 sm:px-14 text-center shadow relative">
        <button
          type="button"
          onClick={() => navigate(`/advertisement/${userid}`)}
          className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 hover:bg-white/20 cursor-pointer flex items-center justify-center"
          aria-label="Back"
        >
          <ArrowLeftIcon className="w-6 h-6 text-white shrink-0" />
        </button>
        <h1 className="text-lg sm:text-2xl font-bold break-words">Your Advertisements</h1>
      </div>

      <div className="max-w-3xl mx-auto p-3 sm:p-4">

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <button
            type="button"
            onClick={() => handleTabChange("running")}
            className={`flex-1 py-2 text-sm sm:text-base rounded-lg font-semibold cursor-pointer ${activeTab === "running" ? "bg-blue-600 text-white" : "bg-white text-gray-600 border"}`}
          >
            Running ({runningCount})
          </button>
          <button
            type="button"
            onClick={() => handleTabChange("expired")}
            className={`flex-1 py-2 text-sm sm:text-base rounded-lg font-semibold cursor-pointer ${activeTab === "expired" ? "bg-blue-600 text-white" : "bg-white text-gray-600 border"}`}
          >
            Expired ({expiredCount})
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <ProgressSpinner />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {ads.length === 0 ? (
                <div className="col-span-full text-center text-gray-400 py-10">
                  No {activeTab} advertisements found
                </div>
              ) : (
                ads.map(renderAdCard)
              )}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-5">
                <button
                  type="button"
                  onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                  className="px-4 py-2 rounded-lg font-semibold bg-white border text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600 font-medium">
                  Page {currentPage + 1} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={currentPage + 1 >= totalPages}
                  className="px-4 py-2 rounded-lg font-semibold bg-white border text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Rerun Modal */}
      <Dialog
        header="Rerun Advertisement"
        visible={!!rerunAd}
        style={{ width: "95vw", maxWidth: "480px" }}
        modal
        draggable={false}
        onHide={closeRerunModal}
      >
        {rerunAd && (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-gray-600 m-0 font-semibold">{rerunAd.company_name}</p>

            {/* Location selection — before package selection */}
            <div className="field">
              <label className="form-label block mb-1">Targeted City</label>
              <input
                ref={cityInputRef}
                type="text"
                placeholder="Search City"
                className="w-full border border-gray-300 rounded px-3 text-sm focus:outline-none focus:border-blue-500"
                style={{ height: "42px" }}
              />
            </div>

            {/* Package selection */}
            <div>
              <label className="form-label block mb-2">Select a Package</label>
              {fetchingPackages ? (
                <div className="flex justify-center py-4">
                  <ProgressSpinner style={{ width: "32px", height: "32px" }} />
                </div>
              ) : packageOptions.length === 0 ? (
                <p className="text-sm text-gray-400 m-0">No packages available</p>
              ) : (
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {packageOptions.map(pkg => {
                    const selected = selectedPackage?._id === pkg._id;
                    return (
                      <div
                        key={pkg._id}
                        onClick={() => setSelectedPackage(pkg)}
                        className={`min-w-[160px] rounded-xl border p-3 cursor-pointer flex-shrink-0 ${selected ? "bg-blue-50 border-blue-500" : "bg-white border-gray-200"}`}
                      >
                        <div className="font-semibold text-gray-800">{pkg.name}</div>
                        <div className="text-blue-600 text-xl font-bold mt-1">₹{pkg.price}</div>
                        <div className="text-gray-500 text-xs mt-1">{pkg.days} Days</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <Button
              label={paying ? "Processing..." : "Pay & Rerun"}
              className="w-full"
              disabled={!selectedPackage || paying}
              loading={paying}
              onClick={handlePayAndRerun}
            />
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default MyAdvertisements;
